/* ============================================================
   CHRIS — THIS IS THE ONLY FILE YOU NORMALLY NEED TO EDIT.
   Every section of your website lives here, top to bottom,
   in the same order it appears on the page.

   HOW TO USE:
   1. Change any text between the quotes "". Keep the quotes
      and the commas at the ends of lines.
   2. Every section has a `display:` switch at the bottom.
      display: true  = section shows on the site
      display: false = section is hidden (safe — nothing breaks)
   3. After editing: save, then `npm start` to preview,
      then `npm run deploy` to put it live. (See GUIDE.md)

   Colors for the whole site: src/_globalColor.scss
   ============================================================ */

import emoji from "react-easy-emoji";
import splashAnimation from "./assets/lottie/splashAnimation"; // Rename to your file name for custom animation

// Splash Screen (the short animated intro when the page loads)

const splashScreen = {
  enabled: true, // set false to disable splash screen
  animation: splashAnimation,
  duration: 2000 // Set animation duration as per your animation
};

// Summary And Greeting Section (the very top of your site)

const illustration = {
  animated: false // false = show your photo (src/assets/images/chrisProfile.jpg); true = show the cartoon animation instead
};

const greeting = {
  username: "Chris Coterillo",
  title: "Hi all, I'm Chris",
  subTitle: emoji(
    "An Industrial Engineer turned Data Analyst with a knack for sales 📊 skilled in Python, SQL, Power BI, and Excel — helping businesses grow through data, dashboards, and smart decisions."
  ),
  // The "Download my resume" button serves the file at:
  //   src/containers/greeting/resume.pdf
  // To update your resume, just replace that file with a new PDF
  // (keep the same name: resume.pdf). Leave resumeLink as any
  // non-empty text to show the button, or "" to hide it.
  resumeLink: "resume.pdf",
  displayGreeting: true // Set false to hide this section, defaults to true
};

// Social Media Links (icons under your name)
// Only the lines you fill in will show. Add e.g. instagram/github later if you want.

const socialMediaLinks = {
  linkedin: "https://www.linkedin.com/in/chris-coterillo/",
  gmail: "coterillochris@gmail.com",
  // instagram: "https://www.instagram.com/yourname/",  // remove the // at the start of a line to turn it on
  // github: "https://github.com/yourusername",
  // facebook: "",
  // twitter: "",
  display: true // Set true to display this section, defaults to false
};

// Skills Section ("What I do")

const skillsSection = {
  title: "What I do",
  subTitle: "DATA ANALYST WHO TURNS MESSY NUMBERS INTO CLEAR BUSINESS DECISIONS",
  skills: [
    emoji(
      "⚡ Build automated dashboards and forecasting models in Power BI and Excel"
    ),
    emoji(
      "⚡ Analyze sales, budgets and KPIs with SQL and Python to find growth opportunities"
    ),
    emoji(
      "⚡ Turn manual, error-prone processes into clean, automated, auditable systems"
    )
  ],

  /* Make Sure to include correct Font Awesome Classname to view your icon
https://fontawesome.com/icons?d=gallery */

  softwareSkills: [
    {
      skillName: "Excel",
      fontAwesomeClassname: "fas fa-file-excel"
    },
    {
      skillName: "Power BI",
      fontAwesomeClassname: "fas fa-chart-bar"
    },
    {
      skillName: "SQL",
      fontAwesomeClassname: "fas fa-database"
    },
    {
      skillName: "Python",
      fontAwesomeClassname: "fab fa-python"
    },
    {
      skillName: "AutoCAD",
      fontAwesomeClassname: "fas fa-drafting-compass"
    }
  ],
  display: true // Set false to hide this section, defaults to true
};

// Education Section

const educationInfo = {
  display: true, // Set false to hide this section, defaults to true
  schools: [
    {
      schoolName: "Georgia Institute of Technology",
      logo: require("./assets/images/georgiaTechLogo.png"), // PLACEHOLDER monogram — swap for the real GT logo when you have it
      subHeader: "Bachelor of Science in Industrial & Systems Engineering",
      duration: "2017 - 2019",
      desc: "Atlanta, GA",
      descBullets: []
    }
  ]
};

// Your top 3 proficiency bars

const techStack = {
  viewSkillBars: true, //Set it to true to show Proficiency Section
  experience: [
    {
      Stack: "Excel & Dashboards",
      progressPercentage: "90%"
    },
    {
      Stack: "Power BI",
      progressPercentage: "85%"
    },
    {
      Stack: "SQL & Python",
      progressPercentage: "75%"
    }
  ],
  displayCodersrank: false // Set true to display codersrank badges section need to changes your username in src/containers/skillProgress/skillProgress.js:17:62, defaults to false
};

// Work experience section
// Logos live in src/assets/images/ — the ones marked PLACEHOLDER are
// simple monogram tiles I generated; replace the files with real logos
// whenever you get them (keep the same filenames and nothing else changes).

const workExperiences = {
  display: true, //Set it to true to show workExperiences Section
  experience: [
    {
      role: "Concierge & Resident Services Associate",
      company: "SLS LUX Brickell",
      companylogo: require("./assets/images/slsLuxLogo.png"), // PLACEHOLDER monogram
      date: "Apr 2025 – Present",
      desc: "Primary point of contact for residents, guests, and vendors at a luxury high-rise property (FirstService Residential), managing all front desk operations in Miami, FL.",
      descBullets: [
        "Independently built an automated Excel tracking system covering 200+ units that standardized manual logs into structured, auditable data for the first time."
      ]
    },
    {
      role: "Freelance Business & Operations Analyst",
      company: "Equipos y Controles Industriales S.A.",
      companylogo: require("./assets/images/eciLogo.png"), // PLACEHOLDER monogram
      date: "Nov 2022 – Jan 2025",
      desc: "Remote contract role for a Bogotá-based industrial company. Built forecasting models in Excel and Power BI for budgeting, supplier cost tracking, and demand planning across 25+ accounts.",
      descBullets: [
        "Maintained executive dashboards tracking 10+ KPIs.",
        "Conducted root cause analysis on underperforming market segments."
      ]
    },
    {
      role: "FP&A Business Analyst",
      company: "Costa Farms",
      companylogo: require("./assets/images/costaFarmsLogo.png"), // PLACEHOLDER monogram
      date: "Feb 2022 – Nov 2022",
      desc: "Developed automated Power BI dashboards and demand forecasting models across 100+ SKUs in Miami, FL, aligning $15M+ in departmental portfolios with corporate revenue targets.",
      descBullets: [
        "Led monthly KPI analysis and variance root cause reviews for business unit leaders."
      ]
    },
    {
      role: "Sales Engineer",
      company: "AmeriKooler",
      companylogo: require("./assets/images/amerikoolerLogo.jpeg"), // real logo (recovered from your old site)
      date: "Sept 2021 – Feb 2022",
      desc: "Spearheaded wholesale market initiatives in Hialeah, FL, contributing to a 30% increase in quote-to-close efficiency across 500+ annual quotes.",
      descBullets: [
        "Engineered automated warranty pricing tools that standardized quoting and enforced pricing governance."
      ]
    },
    {
      role: "Industrial Engineer",
      company: "United Steel Storage",
      companylogo: require("./assets/images/ussLogo.png"), // PLACEHOLDER monogram
      date: "Feb 2021 – May 2021",
      desc: "Designed and optimized warehouse operations in Atlanta, GA using AutoCAD, process simulation, and Lean-based analysis across 8+ client engagements.",
      descBullets: [
        "Generated 15+ technical proposals including SOPs and project planning documentation."
      ]
    },
    {
      role: "Sales & Marketing Intern",
      company: "Equipos y Controles Industriales S.A.",
      companylogo: require("./assets/images/eciLogo.png"), // PLACEHOLDER monogram
      date: "May 2017 – Aug 2018",
      desc: "Supported lead generation and B2B outreach for 100+ prospective customers across Colombia and Peru.",
      descBullets: [
        "Conducted market research on 20+ competitor offerings to inform sales positioning."
      ]
    }
  ]
};

/* Open Source Section — shows your pinned GitHub projects automatically.
   Needs a GitHub token to work; see GUIDE.md section 8. Off until then. */

const openSource = {
  showGithubProfile: "true", // Set true or false to show Contact profile using Github, defaults to true
  display: false // Set false to hide this section, defaults to true
};

// Big projects you have worked on

const bigProjects = {
  title: "Projects",
  subtitle: "DASHBOARDS, MODELS AND TOOLS I'VE BUILT",
  projects: [
    {
      image: require("./assets/images/excelLogo.webp"), // recovered from your old site
      projectName: "<<< EDIT >>> Project Name",
      projectDesc: "<<< EDIT >>> One or two sentences about the project.",
      footerLink: [
        {
          name: "See more",
          url: "" // <<< EDIT >>> link (or delete this block)
        }
        //  you can add extra buttons here.
      ]
    }
  ],
  display: false // <<< Flip to true once you've filled in your projects
};

// Achievement Section — certificates, awards, cool stuff

const achievementSection = {
  title: emoji("Achievements And Certifications 🏆 "),
  subtitle: "<<< EDIT >>> A line about your awards, certificates and wins.",

  achievementsCards: [
    {
      title: "<<< EDIT >>> Achievement Title",
      subtitle: "<<< EDIT >>> One line describing it.",
      image: require("./assets/images/codeInLogo.webp"), // <<< EDIT >>> swap image
      imageAlt: "<<< EDIT >>> short image description",
      footerLink: [
        {
          name: "See it",
          url: "" // <<< EDIT >>> link to certificate/proof (or delete)
        }
      ]
    }
  ],
  display: false // <<< Flip to true once you've filled in your achievements
};

// Blogs Section

const blogSection = {
  title: "Blogs",
  subtitle: "<<< EDIT >>> A line about your writing.",
  displayMediumBlogs: "false", // Set true to display fetched medium blogs instead of hardcoded ones
  blogs: [
    {
      url: "", // <<< EDIT >>> link to a blog post
      title: "<<< EDIT >>> Blog Post Title",
      description: "<<< EDIT >>> One line about the post."
    }
  ],
  display: false // <<< Flip to true if you write blog posts
};

// Talks Section

const talkSection = {
  title: "TALKS",
  subtitle: emoji("<<< EDIT >>> A line about talks you've given 😅"),

  talks: [
    {
      title: "<<< EDIT >>> Talk Title",
      subtitle: "<<< EDIT >>> Where and when",
      slides_url: "", // <<< EDIT >>> link to slides
      event_url: "" // <<< EDIT >>> link to the event
    }
  ],
  display: false // <<< Flip to true if you give talks
};

// Podcast Section

const podcastSection = {
  title: emoji("Podcast 🎙️"),
  subtitle: "<<< EDIT >>> A line about your podcast appearances.",

  // Please Provide with Your Podcast embeded Link
  podcast: [],
  display: false // <<< Flip to true if you have podcast episodes to embed
};

// Resume Section (adds "Resume" to the top navigation bar)

const resumeSection = {
  title: "Resume",
  subtitle: "Feel free to download my resume",

  display: true // Set false to hide this section, defaults to true
};

// Contact Section (bottom of the page)

const contactInfo = {
  title: emoji("Contact Me ☎️"),
  subtitle: "Want to get in touch? My inbox is open for all.",
  number: "", // add your phone number like "+1-555-555-5555" if you ever want it shown
  email_address: "coterillochris@gmail.com"
};

// Twitter Section (embeds your Twitter timeline)

const twitterDetails = {
  userName: "", //Replace with your twitter username without @
  display: false // Set true to display this section, defaults to false
};

const isHireable = true; // Set false if you are not looking for a job. Shows "Open for opportunities: Yes/No" in the GitHub-profile footer.

export {
  illustration,
  greeting,
  socialMediaLinks,
  splashScreen,
  skillsSection,
  educationInfo,
  techStack,
  workExperiences,
  openSource,
  bigProjects,
  achievementSection,
  blogSection,
  talkSection,
  podcastSection,
  contactInfo,
  twitterDetails,
  isHireable,
  resumeSection
};
